import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { getArticleCategoriesApi, getPublishedArticlesApi } from '@/lib/api/blogApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { formatDateAbbreviatedFr } from '@shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowRight, BookOpen, ChevronLeft, ChevronRight, Clock, Mail, Search, Star } from 'lucide-react';

function getCoverUrl(files?: Array<{ id: number; key: string; usage: string }>): string | null {
  const cover = files?.find(f => f.usage === 'ARTICLE_COVER');
  if (!cover) return null;
  const imagesUrl = import.meta.env.VITE_IMAGES_URL as string | undefined;
  if (imagesUrl) return `${imagesUrl}/${cover.key.replace('./uploads/', '')}`;
  const apiUrl = import.meta.env.VITE_URL_API as string;
  return `${apiUrl}/v1/files/${cover.id}/serve`;
}

function updateSearchParams(prev: URLSearchParams, updates: Record<string, string | undefined>): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined || value === '') next.delete(key);
    else next.set(key, value);
  }
  return next;
}

const CATEGORY_COLORS: Record<string, { header: string; badge: string }> = {
  default: {
    header: 'bg-gradient-to-br from-blue-400 to-blue-600',
    badge: 'bg-blue-100 text-blue-700',
  },
};

function getCategoryStyle(color: string | null | undefined) {
  if (color) {
    return {
      header: `bg-gradient-to-br from-[${color}] to-[${color}]`,
      badge: 'bg-slate-100 text-slate-700',
    };
  }
  return CATEGORY_COLORS.default;
}

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlPage = Math.max(1, Number(searchParams.get('page') ?? '1'));
  const urlCategory = searchParams.get('categoryId') ?? '';
  const search = searchParams.get('search') ?? '';

  const [searchInput, setSearchInput] = useState(() => search);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(prev => updateSearchParams(prev, updates), { replace: true });
  }

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateParams({ search: value.trim() || undefined, page: undefined });
    }, 400);
  }

  const queryParams = {
    page: urlPage,
    limit: 9,
    ...(search && { search }),
    ...(urlCategory && { categoryId: Number(urlCategory) }),
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.blog.articles(queryParams),
    queryFn: () => getPublishedArticlesApi(queryParams),
    placeholderData: keepPreviousData,
  });

  // Blog BoulesConnect : on lit la taxonomie plateforme, jamais celle d'un club
  // ou d'un organisateur. L'appel doit etre enveloppe : depuis le Lot A bis,
  // getArticleCategoriesApi prend un scope optionnel, incompatible avec le
  // contexte que react-query passe a une queryFn nue.
  const { data: categories } = useQuery({
    queryKey: queryKeys.blog.categories(),
    queryFn: () => getArticleCategoriesApi(),
  });

  const articles = data?.data ?? [];
  const totalPages = data?.meta?.totalPages ?? 0;
  const featured = articles.find(a => a.featured);
  const gridArticles = articles.filter(a => a !== featured);

  return (
    <>
      <PageMeta
        title='Blog & Actualites'
        description='Retrouvez nos derniers articles, guides et conseils pour organiser vos concours de petanque.'
      />

      {/* Hero */}
      <section className='pt-32 pb-16 px-6'>
        <div className='max-w-5xl mx-auto text-center'>
          <span className='text-blue-600 font-bold text-sm uppercase tracking-wider'>Blog</span>
          <h1 className='text-4xl md:text-6xl font-extrabold text-slate-900 mt-4 mb-6'>
            Blog & <span className='gradient-text'>Actualites</span>
          </h1>
          <p className='text-lg text-slate-500 max-w-2xl mx-auto'>
            Retrouvez nos derniers articles, guides et conseils pour organiser vos concours de petanque.
          </p>
        </div>
      </section>

      {/* Search + Category Filter */}
      <section className='px-6 mb-12'>
        <div className='max-w-5xl mx-auto space-y-4'>
          <div className='relative max-w-md mx-auto'>
            <Search className='absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400' />
            <input
              type='text'
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder='Rechercher un article...'
              className='w-full rounded-full border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none'
            />
          </div>
          <div className='flex flex-wrap justify-center gap-2'>
            <button
              onClick={() => updateParams({ categoryId: undefined, page: undefined })}
              className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                !urlCategory
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              Tous
            </button>
            {(categories ?? []).map(cat => (
              <button
                key={cat.id}
                onClick={() =>
                  updateParams({
                    categoryId: urlCategory === String(cat.id) ? undefined : String(cat.id),
                    page: undefined,
                  })
                }
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                  urlCategory === String(cat.id)
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Loading */}
      {isLoading && (
        <div className='flex justify-center py-20'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent' />
        </div>
      )}

      {/* Featured Article */}
      {!isLoading && featured && (
        <section className='px-6 mb-16'>
          <div className='max-w-6xl mx-auto'>
            <div className='bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl overflow-hidden shadow-2xl shadow-blue-200/50'>
              <div className='grid md:grid-cols-2 gap-0'>
                <div className='p-8 md:p-12 flex flex-col justify-center'>
                  <div className='inline-flex items-center gap-1.5 bg-white/20 backdrop-blur px-3 py-1 rounded-full w-fit mb-6'>
                    <Star className='w-3.5 h-3.5 text-amber-300' />
                    <span className='text-xs font-bold text-white'>Article a la une</span>
                  </div>
                  <h2 className='text-2xl md:text-3xl font-extrabold text-white mb-4 leading-tight'>
                    {featured.title}
                  </h2>
                  <p className='text-blue-100 mb-6 leading-relaxed'>{featured.excerpt}</p>
                  <div className='flex items-center gap-4 text-blue-200 text-sm mb-8'>
                    <span>{formatDateAbbreviatedFr(featured.publishedAt ?? featured.createdAt)}</span>
                    {featured.readTimeMinutes && (
                      <span className='flex items-center gap-1'>
                        <Clock className='w-3.5 h-3.5' /> {featured.readTimeMinutes} min de lecture
                      </span>
                    )}
                  </div>
                  <Link
                    to={ROUTES.ARTICLE.replace(':slug', featured.slug)}
                    className='inline-flex items-center gap-2 bg-white text-blue-700 px-6 py-3 rounded-2xl font-bold text-sm hover:bg-blue-50 transition-colors w-fit'
                  >
                    Lire l&apos;article <ArrowRight className='w-4 h-4' />
                  </Link>
                </div>
                <div className='hidden md:flex items-center justify-center bg-white/5 p-12'>
                  {(() => {
                    const coverUrl = getCoverUrl(featured.files);
                    return coverUrl ? (
                      <img src={coverUrl} alt={featured.title} className='w-full h-64 rounded-2xl object-cover' />
                    ) : (
                      <div className='w-full h-64 rounded-2xl bg-white/10 border-2 border-dashed border-white/20 flex items-center justify-center'>
                        <BookOpen className='w-10 h-10 text-white/30' />
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      {!isLoading && gridArticles.length > 0 && (
        <section className='px-6 mb-16'>
          <div className='max-w-6xl mx-auto'>
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {gridArticles.map(article => {
                const style = getCategoryStyle(article.category?.color);
                return (
                  <Link
                    key={article.id}
                    to={ROUTES.ARTICLE.replace(':slug', article.slug)}
                    className='bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all group'
                  >
                    {(() => {
                      const coverUrl = getCoverUrl(article.files);
                      return coverUrl ? (
                        <div className='h-48 overflow-hidden'>
                          <img
                            src={coverUrl}
                            alt={article.title}
                            className='h-full w-full object-cover transition-transform group-hover:scale-105'
                          />
                        </div>
                      ) : (
                        <div className={`h-48 ${style.header} flex items-center justify-center`}>
                          <BookOpen className='w-8 h-8 text-white/30' />
                        </div>
                      );
                    })()}
                    <div className='p-6'>
                      {article.category && (
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-3 ${style.badge}`}>
                          {article.category.name}
                        </span>
                      )}
                      <h3 className='text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors leading-snug'>
                        {article.title}
                      </h3>
                      <p className='text-sm text-slate-500 mb-4 line-clamp-2'>{article.excerpt}</p>
                      <div className='flex items-center gap-3 text-xs text-slate-400'>
                        <span>{formatDateAbbreviatedFr(article.publishedAt ?? article.createdAt)}</span>
                        {article.readTimeMinutes && (
                          <span className='flex items-center gap-1'>
                            <Clock className='w-3 h-3' /> {article.readTimeMinutes} min
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Empty state */}
      {!isLoading && articles.length === 0 && (
        <section className='px-6 mb-16'>
          <div className='max-w-5xl mx-auto text-center py-20'>
            <p className='text-slate-500 text-lg'>Aucun article trouve.</p>
          </div>
        </section>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <section className='px-6 mb-16'>
          <div className='max-w-6xl mx-auto flex justify-center'>
            <div className='flex items-center gap-1'>
              <button
                onClick={() => updateParams({ page: String(urlPage - 1) })}
                disabled={urlPage <= 1}
                className='w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors disabled:opacity-30'
              >
                <ChevronLeft className='w-4 h-4 text-slate-400' />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => updateParams({ page: String(p) })}
                  className={`w-10 h-10 rounded-xl text-sm font-bold transition-colors ${
                    p === urlPage
                      ? 'bg-blue-600 text-white'
                      : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => updateParams({ page: String(urlPage + 1) })}
                disabled={urlPage >= totalPages}
                className='w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors disabled:opacity-30'
              >
                <ChevronRight className='w-4 h-4 text-slate-400' />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className='px-6 pb-20'>
        <div className='max-w-4xl mx-auto'>
          <div className='bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 md:p-12 text-center'>
            <div className='w-14 h-14 rounded-2xl bg-blue-600/20 flex items-center justify-center mx-auto mb-6'>
              <Mail className='w-7 h-7 text-blue-400' />
            </div>
            <h2 className='text-2xl md:text-3xl font-extrabold text-white mb-3'>Restez informe</h2>
            <p className='text-slate-400 mb-8 max-w-lg mx-auto'>
              Recevez nos derniers articles et actualites directement dans votre boite mail. Pas de spam, promis.
            </p>
            <div className='flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto'>
              <input
                type='email'
                placeholder='votre@email.com'
                className='w-full px-5 py-3.5 rounded-2xl bg-white/10 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50'
              />
              <button className='w-full sm:w-auto px-8 py-3.5 bg-blue-600 text-white rounded-2xl font-bold text-sm hover:bg-blue-700 transition-colors whitespace-nowrap'>
                S&apos;abonner
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
